import {useTranslation} from 'react-i18next';
import moment from 'moment/moment';
import {CarePlanConfigurationFeature} from '../../../backend/enums/care-plan-configuration-feature';
import {CarePlanConfigurationType} from '../../../backend/enums/care-plan-configuration-type';
import {CarePlanConfigurationItem} from '../../../backend/interfaces/care-plan-configuration-item';
import {CarePlanConfiguration} from '../../../backend/interfaces/care-plan-configuration';
import {ActivityType} from '../../../backend/interfaces/activity-type';
import {scheduleReadableExpression, convertArrayToIndex, titleCase} from '../../../utils/functions';
import {translations} from '../../../i18n/translations';

interface RenderPreviewFieldProps {
  activityType: ActivityType;
  field: CarePlanConfiguration;
  transport?: boolean;
  value: any;
}

export const RenderPreviewField = ({
  activityType,
  value,
  field,
  transport,
}: RenderPreviewFieldProps) => {
  const {t} = useTranslation();

  const missingValue = '--';

  if (field.feature) {
    if (field.feature === CarePlanConfigurationFeature.Schedule) {
      return value
        ? scheduleReadableExpression({
            t,
            scheduleDefinition: JSON.parse(String(value)),
          }) || missingValue
        : missingValue;
    }

    if (field.feature === CarePlanConfigurationFeature.Name) {
      return value || activityType.name;
    }

    if (field.feature === CarePlanConfigurationFeature.Notes) {
      return value || missingValue;
    }

    if (field.feature === CarePlanConfigurationFeature.StartsAtDate) {
      if (transport) {
        return (
          moment(value).format(
            `DD/MM/YYYY [${t(
              translations.care_plan_sheet_activities_group_entry_configurationvalue.aivd.at_time
            )}] HH:mm`
          ) || missingValue
        );
      } else {
        return (
          moment(value)
            .format('DD/MM/YYYY')
            .split(' ')
            .map((d) => titleCase(d))
            .join(' ') || missingValue
        );
      }
    }

    if (!transport && field.feature === CarePlanConfigurationFeature.StartsAtTime) {
      return moment(value).format('LT') || missingValue;
    }
  } else {
    if (field.type === CarePlanConfigurationType.String) {
      return value || missingValue;
    }

    if (field.type === CarePlanConfigurationType.Time) {
      return moment(value).format('LT') || missingValue;
    }

    if (field.type === CarePlanConfigurationType.Array && field.multipleValues) {
      const indexedArrayItems = convertArrayToIndex<CarePlanConfigurationItem>(
        field.arrayItems ?? [],
        'id'
      );

      if (Array.isArray(value)) {
        return value.map((itemId: string, index: number) => {
          return (
            <span key={`array_field_${index}`}>
              {indexedArrayItems[itemId].name}
              {value.length > 1 && (
                <>
                  {index < value.length - 2 && ', '}
                  {index === value.length - 2 &&
                    ` ${t(translations.schedule_field.readable_periodicity.and)} `}
                </>
              )}
            </span>
          );
        });
      } else {
        return missingValue;
      }
    }

    if (field.type === CarePlanConfigurationType.Array && !field.multipleValues) {
      const indexedArrayItems = convertArrayToIndex<CarePlanConfigurationItem>(
        field.arrayItems ?? [],
        'id'
      );
      return value ? indexedArrayItems[value].name : missingValue;
    }
  }
};
